import { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import { PaymentRow } from './types';
import { exportPaymentsToXlsx, exportPaymentsToCsv } from './paymentsExportUtils';
import { Export, Eye } from '@phosphor-icons/react';
import { DillButton, DillTable } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';

// ─── Column helpers ───────────────────────────────────────────────────────────

export const PAYMENT_COLUMN_LABELS: Record<keyof PaymentRow, string> = {
  id: 'ID',
  createdAt: 'Created At',
  customerId: 'Customer ID',
  customerName: 'Customer Name',
  totalAmountPaid: 'Total Amount Paid',
  totalDiscountApplied: 'Discount Applied',
  paymentStatus: 'Payment Status',
  paymentErrorMessage: 'Error Message',
  partialPaymentReason: 'Partial Payment Reason',
  paymentMethodType: 'Payment Method Type',
  paymentMethodDetails: 'Payment Method',
  paymentType: 'Payment Type',
  transactionFee: 'Transaction Fee',
  netAmountPaid: 'Net Amount Paid',
  transactionFeeType: 'Fee Type',
  paymentIds: 'Payment IDs',
  cardType: 'Card Type',
};

const textColumn = (
  key: keyof PaymentRow,
  label: string,
  options?: { defaultHidden?: boolean; width?: number }
): DillColumn<PaymentRow> => ({
  key,
  label,
  width: options?.width ?? 140,
  sortable: true,
  filterable: true,
  toggleable: true,
  defaultHidden: options?.defaultHidden,
  render: (value: string) => <div className="text-xs text-gray-500">{value || '—'}</div>,
});

const FEE_TYPE_DISPLAY_LABELS: Record<string, string> = {
  FISERV_FEE_PAYMENT_BUYER: 'CREDIT_CARD_SURCHARGE',
};

const formatFeeTypeLabel = (value: string): string =>
  value
    .split(',')
    .map((type) => (FEE_TYPE_DISPLAY_LABELS[type] ?? type).replace(/_/g, ' '))
    .join(', ');

const numericColumn = (
  key: keyof PaymentRow,
  label: string,
  width = 150
): DillColumn<PaymentRow> => ({
  key,
  label,
  width,
  sortable: true,
  filterable: true,
  numeric: true,
  toggleable: true,
  headerAlign: 'right',
  render: (value: number) => {
    if (value === 0 || value == null) {
      return <div className="text-sm text-gray-300 text-right">—</div>;
    }
    return (
      <div className="text-xs font-medium text-right tabular-nums text-gray-700">
        {value < 0 ? '-' : ''}$
        {Math.abs(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
    );
  },
});

// ─── Column definitions ────────────────────────────────────────────────────────

const getPaymentsColumns = (): DillColumn<PaymentRow>[] => [
  textColumn('id', 'ID', { defaultHidden: true, width: 220 }),
  {
    key: 'createdAt',
    label: 'Created At',
    width: 150,
    sortable: true,
    filterable: true,
    toggleable: true,
    filterType: 'dateRange',
    render: (value: string) => {
      if (!value) return <div className="text-xs text-gray-500">—</div>;
      return <div className="text-xs text-gray-500">{moment(value).format('MM/DD/YYYY')}</div>;
    },
  },
  textColumn('customerId', 'Customer ID', { width: 150 }),
  textColumn('customerName', 'Customer Name', { width: 220 }),
  numericColumn('totalAmountPaid', 'Total Paid', 160),
  numericColumn('totalDiscountApplied', 'Discount', 150),
  textColumn('paymentStatus', 'Status', { width: 150 }),
  textColumn('paymentErrorMessage', 'Error', { defaultHidden: true, width: 180 }),
  textColumn('partialPaymentReason', 'Partial Reason', { defaultHidden: true, width: 200 }),
  {
    key: 'paymentMethodType',
    label: 'Method Type',
    width: 170,
    sortable: true,
    filterable: true,
    toggleable: true,
    defaultHidden: true,
    getFilterLabel: (value: string) =>
      value === 'CREDIT_CARD' ? 'CARD' : value.replace(/_/g, ' '),
    render: (value: string) => {
      if (!value) return <div className="text-xs text-gray-500">—</div>;
      const display = value === 'CREDIT_CARD' ? 'CARD' : value.replace(/_/g, ' ');
      return <div className="text-xs text-gray-500">{display}</div>;
    },
  },
  textColumn('cardType', 'Card Type', { defaultHidden: true, width: 150 }),
  textColumn('paymentMethodDetails', 'Payment Method', { width: 180 }),
  {
    key: 'paymentType',
    label: 'Payment Type',
    width: 240,
    sortable: true,
    filterable: true,
    toggleable: true,
    getFilterLabel: (value: string) => (value === 'WITHDRAW' ? 'PAYOUT' : value),
    render: (value: string) => (
      <div className="text-xs text-gray-500">
        {(value === 'WITHDRAW' ? 'PAYOUT' : value) || '—'}
      </div>
    ),
  },
  numericColumn('transactionFee', 'Fee', 140),
  numericColumn('netAmountPaid', 'Net Paid', 150),
  {
    key: 'transactionFeeType',
    label: 'Fee Type',
    width: 260,
    sortable: true,
    filterable: true,
    toggleable: true,
    defaultHidden: true,
    getFilterLabel: (value: string) => formatFeeTypeLabel(value),
    render: (value: string, row: PaymentRow) => (
      <div className="text-xs text-gray-500">
        {row.transactionFee ? (value ? formatFeeTypeLabel(value) : '—') : '—'}
      </div>
    ),
  },
  textColumn('paymentIds', 'Payment IDs', { defaultHidden: true, width: 240 }),
];

// ─── Component ─────────────────────────────────────────────────────────────────

interface PaymentsTableProps {
  data: PaymentRow[];
  isLoading?: boolean;
}

export const PaymentsTable = ({ data, isLoading }: PaymentsTableProps) => {
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  // Rows after the table's search/filters/sorting are applied — what we export.
  const [filteredData, setFilteredData] = useState<PaymentRow[]>(data);
  const exportRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  const allColumns = useMemo(() => getPaymentsColumns(), []);

  const [hiddenCols, setHiddenCols] = useState<Set<string>>(
    () => new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key))
  );

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => !hiddenCols.has(c.key)),
    [allColumns, hiddenCols]
  );

  const toggleColumn = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        const visibleCount = allColumns.filter((c) => !next.has(c.key)).length;
        if (visibleCount <= 3) return prev;
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setIsExportOpen(false);
      }
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setIsColumnsOpen(false);
      }
    };

    if (isExportOpen || isColumnsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportOpen, isColumnsOpen]);

  const handleExportXlsx = () => {
    exportPaymentsToXlsx(
      filteredData,
      PAYMENT_COLUMN_LABELS,
      visibleColumns.map((c) => c.key as keyof PaymentRow)
    );
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    exportPaymentsToCsv(
      filteredData,
      PAYMENT_COLUMN_LABELS,
      visibleColumns.map((c) => c.key as keyof PaymentRow)
    );
    setIsExportOpen(false);
  };

  return (
    <div className="p-6 flex flex-col">
      <div className="mb-4">
        <p className="text-xs text-gray-500">
          Export payment transaction details including net amounts received, processing fees, and
          payment methods. Use this to reconcile deposits with your bank statements.
        </p>
      </div>

      <DillTable<PaymentRow>
        columns={visibleColumns}
        data={data}
        onProcessedDataChange={setFilteredData}
        rowKey={(r) => r.id}
        verticalDividers
        search
        searchPlaceholder="Search payments…"
        pagination
        pageSize={25}
        rowHeight={35}
        scrollX
        showActiveFilters
        striped={false}
        maxHeight="calc(100vh - 280px)"
        loading={isLoading}
        loadingText="Loading payments..."
        emptyMessage="No payments match your filters"
        showCount={false}
        toolbarRight={
          <div className="flex gap-2">
            {/* Column visibility dropdown */}
            <div className="relative" ref={columnsRef}>
              <DillButton
                label="Columns"
                onClick={() => setIsColumnsOpen(!isColumnsOpen)}
                icon={<Eye size={14} />}
                variant="ghost"
              />
              {isColumnsOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-56 max-h-[400px] overflow-y-auto">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 flex items-center justify-between">
                    <span>Show/Hide Columns</span>
                    <button
                      onClick={() => setHiddenCols(new Set())}
                      className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 normal-case tracking-normal">
                      Select All
                    </button>
                  </div>
                  {allColumns.map((col) => {
                    const isVisible = !hiddenCols.has(col.key);
                    const canHide = isVisible && visibleColumns.length <= 3;
                    return (
                      <label
                        key={col.key}
                        className={`flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                          canHide ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}>
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => toggleColumn(col.key)}
                          disabled={canHide}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-100/80 disabled:opacity-50"
                        />
                        <span className="flex-1">{col.label}</span>
                        {canHide && (
                          <span className="text-[10px] text-gray-400">Min 3 required</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              className="relative"
              ref={exportRef}
              title={isLoading ? 'Still loading payments…' : undefined}>
              <DillButton
                label="Export"
                onClick={() => setIsExportOpen(!isExportOpen)}
                icon={<Export size={14} />}
                variant="primary"
                isDisabled={isLoading}
              />
              {isExportOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
                  <button
                    onClick={handleExportXlsx}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as XLSX
                  </button>
                  <button
                    onClick={handleExportCsv}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />
    </div>
  );
};
